/**
 * @file index.tsx
 *
 * @fileoverview Renders an image optimized in case the .env file is set to do so.
 */
import NextImage from 'next/image';
import { Figure } from '~/vcl/atoms/30-figure';
export interface ImageProps {
  /**
   * The alternate text for the image.
   */
  alt: string;
  /**
   * The itle for the image.
   */
  title?: string;
  /**
   * The external src for the image.
   */
  src: string;
  /**
   * The width property for this image.
   */
  width: number | null | undefined;
  /**
   * The height property for this image.
   */
  height: number | null | undefined;
  /**
   * Portrait image source.
   */
  portraitSrc?: string;
  /**
   * Priority will add fetchPriority to the image.
   *
   * @default "false"
   */
  priority?: boolean;
  /**
   * The caption text.
   */
  caption?: string;
  /**
   * Declares the quality of the generated image.
   */
  quality?: number;
  /**
   * Convert the image format to webp.
   *
   * @default "true"
   */
  convertFormat?: boolean;
  /**
   * Optional click handler for the image.
   */
  onClick?: () => void;
}

export const Image = ({
  alt,
  title,
  src,
  width,
  height,
  priority = false,
  caption,
  quality = 75,
  convertFormat = true,
  onClick,
}: ImageProps) => {
  const optimizedImages = process.env.OPTIMIZE_IMAGES === 'TRUE' ? true : false;

  return (
    <ImageWrapper caption={caption}>
      <NextImage
        src={decodeURIComponent(src)} // Next image expects the decoded URI to fetch.
        alt={alt}
        {...(title && { title })}
        {...(!width || !height
          ? { sizes: '100vw', fill: true }
          : { width, height })}
        {...(priority
          ? { fetchPriority: 'high', preload: true, loading: 'eager' }
          : false)}
        quality={quality}
        unoptimized={!convertFormat || !optimizedImages}
        {...(onClick && { onClick })}
      />
    </ImageWrapper>
  );
};

export default Image;

function ImageWrapper({ children, caption }) {
  return caption ? (
    <Figure caption={caption}>{children}</Figure>
  ) : (
    <>{children}</>
  );
}
